/**
 * Supplies peers' rank/level to a subtree (specs/rank-on-social-screens AC-10).
 *
 * Why a context and not props: the World Cup leaderboard's avatar, row card, podium card and
 * self bar are four components deep inside a shared, recycling FlashList, and the same
 * component tree also renders the squad standings — which this slice deliberately leaves
 * alone (spec §Non-Goals). Threading a lookup through every layer would touch six files and
 * still need a flag to keep it off the squad board.
 *
 * The default lookup returns null, so a subtree with no provider renders no crests. Scope is
 * therefore decided by one thing: whether a screen mounts the provider.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { usePlayerRanks, type PlayerRankLookup } from '../hooks/usePlayerRanks';

const NO_RANKS: PlayerRankLookup = () => null;

const PlayerRanksContext = createContext<PlayerRankLookup>(NO_RANKS);

export const PlayerRanksProvider: React.FC<{
  /** Players on screen. One batch request; capped by the hook. */
  userIds: string[];
  children: React.ReactNode;
}> = ({ userIds, children }) => {
  const { getPlayerRank } = usePlayerRanks(userIds);
  const value = useMemo(() => getPlayerRank, [getPlayerRank]);
  return <PlayerRanksContext.Provider value={value}>{children}</PlayerRanksContext.Provider>;
};

/** Returns a lookup that yields null when no provider is mounted (i.e. crests are off here). */
export function usePlayerRankLookup(): PlayerRankLookup {
  return useContext(PlayerRanksContext);
}
